import axios, { type AxiosError } from 'axios';
import { API_URL } from '@/lib/constants';
import { useAuthStore } from '@/stores/auth.store';

const api = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
});

/**
 * Hasil refresh:
 *   - string    → token baru
 *   - 'transient' → server sibuk / jaringan (503 db_busy, 5xx, offline): sesi
 *     DIPERTAHANKAN, request asli gagal lembut (insiden 9 Sep 2026: saat D1
 *     overload karyawan ter-logout dan tak bisa clock-in)
 *   - null      → refresh token memang ditolak (401/403) → sesi habis
 */
type RefreshResult = string | 'transient' | null;
let refreshInFlight: Promise<RefreshResult> | null = null;

async function refreshAccessToken(): Promise<RefreshResult> {
    if (refreshInFlight) return refreshInFlight;
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) return null;

    refreshInFlight = (async (): Promise<RefreshResult> => {
        try {
            const { data } = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
            const payload = (data?.data ?? data) as { accessToken: string; refreshToken: string };
            useAuthStore.getState().setTokens(payload.accessToken, payload.refreshToken);
            return payload.accessToken;
        } catch (err) {
            const status = (err as AxiosError)?.response?.status;
            if (status === 401 || status === 403) {
                useAuthStore.getState().clear();
                return null;
            }
            return 'transient';
        } finally {
            refreshInFlight = null;
        }
    })();
    return refreshInFlight;
}

api.interceptors.request.use((config) => {
    const url = (config.url || '').toString();
    const skipAuth = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/sso-exchange');
    if (skipAuth) return config;
    const token = useAuthStore.getState().accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (response) => {
        // Backend wrap dengan { success, data, ... } — auto-unwrap
        if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data) {
            response.data = (response.data as any).data;
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest: any = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            const result = await refreshAccessToken();
            if (result && result !== 'transient') {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${result}`;
                return api(originalRequest);
            }
            if (result === 'transient') {
                // Server sibuk: JANGAN logout — biarkan caller menampilkan error & coba lagi.
                return Promise.reject(error);
            }
            // Refresh ditolak (401/403) — sesi habis → ke /login
            window.location.href = '/login';
        }
        return Promise.reject(error);
    },
);

export default api;
